import React from 'react'
import { createContext, useContext } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { setIdentity, setSettings } from 'redux/actions'
import { ReactReduxContext } from 'react-redux'
import Store from 'redux/Store'
import { setCustomData, setViewMode } from 'shared/redux/actions'
import AccessDenied from '../AccessDenied'
import { roles as allRoles, rolesInherit } from 'consts'

interface AuthContextType {
  signIn: (
    username: string,
    token: string,
    customData: object | null,
    settings: {} | null,
    callback: VoidFunction,
  ) => boolean
  signOut: (callback: VoidFunction) => boolean
}

let AuthContext = createContext<AuthContextType>(null!)

function AuthProvider({ children }: { children: React.ReactNode }) {
  let signIn = (
    username: string,
    token: string,
    customData: object | null,
    settings: {} | null,
    callback: VoidFunction,
  ) => {
    if (Store.dispatch(setIdentity(username, token))) {
      Store.dispatch(setCustomData(customData))
      Store.dispatch(setSettings(settings))
      // assign role as view mode for switchable layout
      if (customData && typeof customData['role'] !== 'undefined') {
        Store.dispatch(setViewMode(customData['role']))
      }
      callback()
      return true
    } else {
      return false
    }
  }

  let signOut = (callback: VoidFunction) => {
    Store.dispatch(setIdentity('', false))
    Store.dispatch(setCustomData(null))
    Store.dispatch(setSettings(null))
    callback()
    return true
  }

  let value = { signIn, signOut }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function useAuth() {
  return useContext(AuthContext)
}

function RequireAuth({ children, roles }: { children: JSX.Element; roles: string[] | undefined }) {
  let auth = useAuth()
  let location = useLocation()
  const { store } = useContext(ReactReduxContext)
  let identity = store.getState().identity
  const userRole = store.getState().customData?.customData?.role
  if (identity.username !== '' && identity.token !== false) {
    if (roles === undefined || roles.includes(userRole)) {
      return children
    } else {
      // find inherit roles
      let isValid = false
      const roleName = Object.keys(allRoles).find((key) => allRoles[key] === userRole)

      if (roleName !== undefined && rolesInherit[roleName] !== undefined) {
        if (rolesInherit[roleName].indexOf(userRole)) {
          isValid = true
        }
      }

      return isValid ? children : <AccessDenied />
    }
  } else {
    return <Navigate to='/login' state={{ from: location }} replace />
  }
}

function getToken() {
  return Store.getState().identity.token
}

function getCustomData(key: string) {
  const customData = Store.getState().customData?.customData
  return customData?.hasOwnProperty(key) ? customData[key] : null
}

function getSettingsValue(key: string) {
  const settings = Store.getState().settings
  return settings?.hasOwnProperty(key) ? settings[key] : null
}

function setSettingsValue(key: string, value: any) {
  Store.dispatch(setSettings({ [key]: value }))
}

function setAppViewMode(viewMode: string) {
  Store.dispatch(setViewMode(viewMode))
  return viewMode
}

function getAppViewMode() {
  const viewMode = Store.getState().viewMode?.mode
  return viewMode
}

function getAllCustomData() {
  return Store.getState().customData?.customData
}

export {
  AuthContext,
  AuthProvider,
  useAuth,
  RequireAuth,
  getToken,
  getCustomData,
  getAllCustomData,
  getAppViewMode,
  setAppViewMode,
  getSettingsValue,
  setSettingsValue
}
